import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { openAppWindow } from "src/lib/runtime/appWindows";
import { isTauriRuntime } from "src/lib/runtime/environment";

/**
 * Open what a notification card points at.
 *
 * A card is ambient: it arrives unasked, over whatever the person is in the middle of. On the
 * desktop, acting on one therefore opens its own window rather than steering the window they were
 * working in — the same reason a card is not allowed to navigate you away mid-sentence. In the
 * browser, which has one window, it navigates as before.
 *
 * The policy lives here rather than in each card so a new card inherits it by calling this, and
 * cards whose target is a surface with its own opener (a chat, a compose, a confirmation) keep
 * using that opener — it makes this same decision for that surface.
 */
export function useOpenNotificationTarget() {
  const navigate = useNavigate();

  const openRoute = useCallback(
    (path: string) => {
      if (!isTauriRuntime()) {
        navigate(path);
        return;
      }
      void openAppWindow(path).catch((error) => {
        console.warn("Could not open a window for a notification; navigating instead.", error);
        navigate(path);
      });
    },
    [navigate]
  );

  return { openRoute };
}
